import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { JointSelectorPsA } from "./joint-selector-psa";

function ScaleSelect({ label, value, onChange, options }: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  options: { label: string; value: number }[];
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
      <div className="grid grid-cols-5 gap-1">
        {options.map((o) => (
          <button key={o.value} onClick={() => onChange(o.value)}
            className={`text-xs py-1.5 rounded border transition-colors ${value === o.value ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-600 border-slate-200 hover:border-teal-300"}`}>
            {o.value}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-slate-400 mt-0.5">
        <span>{options[0].label}</span>
        <span>{options[options.length - 1].label}</span>
      </div>
    </div>
  );
}

const SCALE5 = [
  { label: "Excellent", value: 1 },
  { label: "",          value: 2 },
  { label: "",          value: 3 },
  { label: "",          value: 4 },
  { label: "Poor",      value: 5 },
];

export function PsARCCalc() {
  const [useMap, setUseMap] = useState(false);

  const [bPatGlobal,  setBPatGlobal]  = useState(3);
  const [bPhysGlobal, setBPhysGlobal] = useState(3);
  const [bTjc,        setBTjc]        = useState(0);
  const [bSjc,        setBSjc]        = useState(0);

  const [fPatGlobal,  setFPatGlobal]  = useState(3);
  const [fPhysGlobal, setFPhysGlobal] = useState(3);
  const [fTjc,        setFTjc]        = useState(0);
  const [fSjc,        setFSjc]        = useState(0);

  const pctChange = (b: number, f: number) =>
    b > 0 ? `${Math.round(((b - f) / b) * 100)}% change` : "—";

  const improvedPatGlobal  = (bPatGlobal - fPatGlobal) >= 1;
  const improvedPhysGlobal = (bPhysGlobal - fPhysGlobal) >= 1;
  const improvedTjc        = bTjc > 0 && ((bTjc - fTjc) / bTjc) >= 0.3;
  const improvedSjc        = bSjc > 0 && ((bSjc - fSjc) / bSjc) >= 0.3;

  const worsenedPatGlobal  = (fPatGlobal - bPatGlobal) >= 1;
  const worsenedPhysGlobal = (fPhysGlobal - bPhysGlobal) >= 1;
  const worsenedTjc        = bTjc > 0 && fTjc > bTjc;
  const worsenedSjc        = bSjc > 0 && fSjc > bSjc;

  const improvedCount = [improvedPatGlobal, improvedPhysGlobal, improvedTjc, improvedSjc].filter(Boolean).length;
  const anyWorsened   = worsenedPatGlobal || worsenedPhysGlobal || worsenedTjc || worsenedSjc;
  const psarcResponse = improvedCount >= 2 && !anyWorsened;

  const criteria = [
    { label: "Patient global",   sub: `≥1 point improvement · Baseline ${bPatGlobal}/5 → Follow-up ${fPatGlobal}/5`,  improved: improvedPatGlobal,  worsened: worsenedPatGlobal },
    { label: "Physician global", sub: `≥1 point improvement · Baseline ${bPhysGlobal}/5 → Follow-up ${fPhysGlobal}/5`, improved: improvedPhysGlobal, worsened: worsenedPhysGlobal },
    { label: "Tender joints",    sub: `≥30% improvement · ${bTjc} → ${fTjc} (${pctChange(bTjc, fTjc)})`,             improved: improvedTjc,        worsened: worsenedTjc },
    { label: "Swollen joints",   sub: `≥30% improvement · ${bSjc} → ${fSjc} (${pctChange(bSjc, fSjc)})`,             improved: improvedSjc,        worsened: worsenedSjc },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">PsARC</CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Badge variant="outline">PsA</Badge>
            <Badge variant="outline">Response criteria</Badge>
            <Badge variant="outline">TJC68 / SJC66</Badge>
          </div>
        </div>
        <p className="text-xs text-slate-500">
          Psoriatic Arthritis Response Criteria — improvement in ≥2 of 4 measures
          (including ≥1 joint measure) with no worsening in any criterion.
          Joint counts use the full peripheral 68/66 joint set.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">

        {/* Joint map toggle */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setUseMap(!useMap)}
            className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${useMap ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-600 border-slate-200 hover:border-teal-300"}`}
          >
            {useMap ? "✓ Using Joint Maps (60 joints)" : "Use Visual Joint Maps"}
          </button>
          {!useMap && <span className="text-xs text-slate-400">Or enter counts manually below</span>}
        </div>

        {/* Global assessments — always manual */}
        <div className="grid sm:grid-cols-2 gap-6">
          <div className="space-y-3">
            <p className="text-xs font-bold text-slate-700 uppercase tracking-wide border-b pb-1">Baseline visit</p>
            <ScaleSelect label="Patient global (1=Excellent → 5=Poor)" value={bPatGlobal} onChange={setBPatGlobal} options={SCALE5} />
            <ScaleSelect label="Physician global (1=Excellent → 5=Poor)" value={bPhysGlobal} onChange={setBPhysGlobal} options={SCALE5} />
            {!useMap && (
              <>
                <div>
                  <label htmlFor="psarc-b-tjc" className="block text-xs font-medium text-slate-700 mb-1">Tender joint count (TJC68)</label>
                  <input id="psarc-b-tjc" type="number" min={0} max={68} value={bTjc}
                    onChange={(e) => setBTjc(parseInt(e.target.value) || 0)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>
                <div>
                  <label htmlFor="psarc-b-sjc" className="block text-xs font-medium text-slate-700 mb-1">Swollen joint count (SJC66)</label>
                  <input id="psarc-b-sjc" type="number" min={0} max={66} value={bSjc}
                    onChange={(e) => setBSjc(parseInt(e.target.value) || 0)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>
              </>
            )}
            {useMap && (
              <div className="text-xs text-slate-500 bg-slate-100 rounded-lg px-3 py-2">
                TJC: <strong className="text-orange-600">{bTjc}</strong> · SJC: <strong className="text-blue-600">{bSjc}</strong>
                <span className="text-slate-400 ml-2">(from map below)</span>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <p className="text-xs font-bold text-slate-700 uppercase tracking-wide border-b pb-1">Follow-up visit</p>
            <ScaleSelect label="Patient global (1=Excellent → 5=Poor)" value={fPatGlobal} onChange={setFPatGlobal} options={SCALE5} />
            <ScaleSelect label="Physician global (1=Excellent → 5=Poor)" value={fPhysGlobal} onChange={setFPhysGlobal} options={SCALE5} />
            {!useMap && (
              <>
                <div>
                  <label htmlFor="psarc-f-tjc" className="block text-xs font-medium text-slate-700 mb-1">Tender joint count (TJC68)</label>
                  <input id="psarc-f-tjc" type="number" min={0} max={68} value={fTjc}
                    onChange={(e) => setFTjc(parseInt(e.target.value) || 0)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>
                <div>
                  <label htmlFor="psarc-f-sjc" className="block text-xs font-medium text-slate-700 mb-1">Swollen joint count (SJC66)</label>
                  <input id="psarc-f-sjc" type="number" min={0} max={66} value={fSjc}
                    onChange={(e) => setFSjc(parseInt(e.target.value) || 0)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>
              </>
            )}
            {useMap && (
              <div className="text-xs text-slate-500 bg-slate-100 rounded-lg px-3 py-2">
                TJC: <strong className="text-orange-600">{fTjc}</strong> · SJC: <strong className="text-blue-600">{fSjc}</strong>
                <span className="text-slate-400 ml-2">(from map below)</span>
              </div>
            )}
          </div>
        </div>

        {/* Dual 60-joint maps */}
        {useMap && (
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-xs font-bold text-teal-700 uppercase tracking-wide text-center pb-1 border-b border-teal-100">
                Baseline visit — mark joints
              </p>
              <JointSelectorPsA
                label="Baseline — 60-Joint Map"
                onChange={(t, s) => { setBTjc(t); setBSjc(s); }}
              />
            </div>
            <div className="space-y-1">
              <p className="text-xs font-bold text-indigo-700 uppercase tracking-wide text-center pb-1 border-b border-indigo-100">
                Follow-up visit — mark joints
              </p>
              <JointSelectorPsA
                label="Follow-up — 60-Joint Map"
                onChange={(t, s) => { setFTjc(t); setFSjc(s); }}
              />
            </div>
          </div>
        )}

        {/* Criteria breakdown */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Response criteria check</p>
          {criteria.map((c, i) => (
            <div key={i}
              className={`flex items-center justify-between rounded-lg px-3 py-2.5 text-xs border ${c.worsened ? "bg-red-50 border-red-200" : c.improved ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
              <div>
                <p className={`font-medium ${c.worsened ? "text-red-700" : c.improved ? "text-emerald-700" : "text-slate-600"}`}>{c.label}</p>
                <p className="text-slate-400 mt-0.5">{c.sub}</p>
              </div>
              <span className={`font-bold shrink-0 ml-3 ${c.worsened ? "text-red-600" : c.improved ? "text-emerald-700" : "text-slate-400"}`}>
                {c.worsened ? "✗ Worse" : c.improved ? "✓ Met" : "— Not met"}
              </span>
            </div>
          ))}
        </div>

        {/* Result */}
        <div className={`rounded-xl p-4 text-center border-2 ${psarcResponse ? "bg-emerald-50 border-emerald-300" : anyWorsened ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"}`}>
          <p className={`text-lg font-bold ${psarcResponse ? "text-emerald-700" : anyWorsened ? "text-red-600" : "text-slate-500"}`}>
            {psarcResponse
              ? "✓ PsARC Response Achieved"
              : anyWorsened
              ? "✗ No response — worsening detected"
              : "✗ No response — insufficient improvement"}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {improvedCount} of 4 criteria met · {anyWorsened ? "Worsening in ≥1 criterion" : "No worsening detected"}
          </p>
        </div>

        <p className="text-xs text-slate-400">
          PsARC joint maps cover 60 major peripheral joints (shoulders, elbows, wrists, MCPs, PIPs, DIPs, hips, knees, ankles, MTPs, foot PIPs).
          This is a subset of the full TJC68/SJC66 — formal clinical trial assessment may include temporomandibular, sternoclavicular, and acromioclavicular joints.
        </p>
      </CardContent>
    </Card>
  );
}
